/* eslint-disable max-classes-per-file */
import { DataFlowDataType, getDataFlowExpressionBuilderTypeIcon, IDataFlowDataType } from 'app/model/dataflow/dataflow-datatype';
import { ColumnReference } from '../dataset/columnReference';
import { DataFlowColumn, IBasedOnEntity } from '../dataset/dataflowcolumn';
import { BasicExpression } from './expressionTypes';
import { DataFlowExpressionFunctions } from './dataflow-expression-functions';
import { DataFlowFunctionArgument } from './dataflow-function-argument';
import { DataFlowArgument, DataFlowFilterType } from './dataflow-argument';
import { OwnedSet } from 'shell-models-lib';
import { DataFlowParameter } from './dataflow-parameter';
import { DataFlowExpression } from './dataflowexpression';
import { DataFlowStream } from './transformation';
import { DataFlowSink } from './dataflow-sink';

export {
    dataFlowPatternParameter, dataFlowPatternNameParameter, dataFlowPatternTypeParameter,
    dataFlowPatternStreamParameter, dataFlowPatternOriginParameter, dataFlowPatternPositionParameter
} from 'worker/app-workers/shared/constants';

export interface IUsedIdentifier {
    expression: BasicExpression[];
}

interface IExpressionColumnReference {
    column: IUsedColumnIdentifier;
    expression: BasicExpression;
}

export interface IUsedColumnIdentifier extends IUsedIdentifier {
    column: DataFlowColumnArgument;
    fields?: IBasedOnEntity[];
}

export interface IUsedParameterIdentifier extends IUsedIdentifier {
    parameter: DataFlowParameterArgument;
}

export interface IUsedCacheSinkIdentifier extends IUsedIdentifier {
    cacheSink: DataFlowCacheSinkArgument;
}

export interface IUsedFunction extends IUsedIdentifier {
    dataFlowFunction: DataFlowFunctionArgument;
}

export interface IUsedIdentifiers {
    dataFlowFunctions: IUsedFunction[];
    dataFlowParameters: IUsedParameterIdentifier[];
    dataFlowColumns: IUsedColumnIdentifier[];
    dataFlowCacheSinks: IUsedCacheSinkIdentifier[];
}

export interface IDataFlowArguments {
    dataFlowFunctions: DataFlowFunctionArgument[];
    dataFlowColumns: DataFlowColumnArgument[];
    dataFlowTransientColumns: DataFlowColumnArgument[];
    dataFlowParameters: DataFlowParameterArgument[];
    dataFlowCacheSinks: DataFlowCacheSinkArgument[];
    dataFlowTypes?: DataFlowTypeArgument[];
    dataFlowUserDefinedArguments?: DataFlowUserDefinedArgument[];
    dataFlowUserDefinedFunctions?: DataFlowFunctionArgument[];
}

export class DataFlowColumnArgument extends DataFlowArgument {
    constructor(name: string, type: DataFlowDataType, public dataFlowColumn: DataFlowColumn, private _insertText?: string, filterType?: DataFlowFilterType, public inputStream?: DataFlowStream) {
        super(name, type, filterType);
    }

    public get inputStreamName(): string {
        return this.inputStream && this.inputStream.qualifiedName || '';
    }

    public getInsertText(): string {
        const insertText = this._insertText || this.dataFlowColumn.serializableColumnName;
        const prefix = this.dataFlowColumn && this.dataFlowColumn.isTransient ? ':' : '';
        return `${prefix}${insertText}`;
    }

    public getTypeIcon(): string {
        return `icon ${getDataFlowExpressionBuilderTypeIcon(this.dataFlowColumn.columnType)}`;
    }

    public getParameterLabels(): string[] {
        return [];
    }

    public getParameterIcons(): string[] {
        return [];
    }

    public getExpressionTooltip(): string {
        return '';
    }

}

export class DataFlowTypeArgument extends DataFlowArgument {
    constructor(name: string, public dataFlowType: IDataFlowDataType, private _insertText?: string) {
        super(name, 'string', 'string');
    }

    public getInsertText(): string {
        return this._insertText || this.name;
    }

    public getTypeIcon(): string {
        return '';
    }

    public getExpressionTooltip(): string {
        return '';
    }

    public getParameterLabels(): string[] {
        return [];
    }

    public getParameterIcons(): string[] {
        return [];
    }
}

export class DataFlowCacheSinkArgument extends DataFlowArgument {
    constructor(name: string, public cacheSink: DataFlowSink, private _insertText?: string) {
        super(name, 'array', 'array');
    }

    public getInsertText(): string {
        return this._insertText || this.name;
    }

    public getTypeIcon(): string {
        return `icon svg-i-16x16-Array`;
    }

    public getExpressionTooltip(): string {
        return '';
    }

    public getParameterLabels(): string[] {
        return [];
    }

    public getParameterIcons(): string[] {
        return [];
    }
}

export class DataFlowUserDefinedArgument extends DataFlowArgument {
    constructor(name: string, type: DataFlowDataType) {
        super(name, type, 'string');
    }

    public getInsertText(): string {
        return this.name;
    }

    public getTypeIcon(): string {
        return `icon ${getDataFlowExpressionBuilderTypeIcon(this.type)}`;
    }

    public getExpressionTooltip(): string {
        return '';
    }

    public getParameterLabels(): string[] {
        return [];
    }

    public getParameterIcons(): string[] {
        return [];
    }
}

export class DataFlowParameterArgument extends DataFlowArgument {
    // eslint-disable-next-line
    constructor(name: string, type: DataFlowDataType, public dataFlowParameter: DataFlowParameter, private _insertText?: string, filterType?: DataFlowFilterType) {
        super(name, type, filterType);
    }
    public getInsertText(): string {
        return this._insertText || this.name;
    }

    public getTypeIcon(): string {
        return `icon ${getDataFlowExpressionBuilderTypeIcon(this.type)}}`;
    }

    public getParameterLabels(): string[] {
        return [];
    }

    public getParameterIcons(): string[] {
        return [];
    }

    public getExpressionTooltip(): string {
        return '';
    }
}

export function convertColumnRefToDataFlowColumn(columnRefs: ColumnReference[]): DataFlowColumnArgument[] {
    const columnArguments: DataFlowColumnArgument[] = [];

    for (const columnRef of columnRefs) {
        const columnArgument = new DataFlowColumnArgument(
            columnRef.name,
            columnRef.column.dataFlowColumnType ? columnRef.column.dataFlowColumnType.typeString : 'any',
            columnRef.column,
            columnRef.serializableName,
            null,
            columnRef.inputStream
        );
        columnArguments.push(columnArgument);

    }
    return columnArguments;
}

export function convertCacheSinksToArguments(cacheSinks: OwnedSet<DataFlowSink>): DataFlowCacheSinkArgument[] {
    if (!cacheSinks) {
        return [];
    }
    const cacheSinkArgs: DataFlowCacheSinkArgument[] = [];
    cacheSinks.forEach((cacheSink) => {
        cacheSinkArgs.push(new DataFlowCacheSinkArgument(cacheSink.name, cacheSink, `${cacheSink.name}#`));
    });
    return cacheSinkArgs;
}

export function getSortedExpressionsInColumnArguments(dataFlowColumns: IUsedColumnIdentifier[]): IExpressionColumnReference[] {
    const expressions: IExpressionColumnReference[] = [];
    dataFlowColumns.forEach((column) => {
        column.expression.forEach((expression) => {
            expressions.push({ column, expression });
        });
    });
    expressions.sort((x, y) => x.expression.start < y.expression.start ? -1 : 1);
    return expressions;
}

export function convertParametersToArguments(parameters: OwnedSet<DataFlowParameter>): DataFlowParameterArgument[] {
    return parameters ? Array.from(parameters).map((param) => new DataFlowParameterArgument(param.name, param._type, param, `$${param.name}`)) : [];
}

export function isTruthy(expression: DataFlowExpression): boolean {
    return expression.expression && expression.expression !== 'false';
}

export function generateGrammarForColumns(
    distinctInputColumnRefs: ColumnReference[],
    cacheSinks: OwnedSet<DataFlowSink>,
    parameters: OwnedSet<DataFlowParameter>,
    filterTypes?: string[],
    limitedfunctions?: DataFlowFunctionArgument[],
    addWindowFunctions: boolean = false,
    addAggregateFunctions: boolean = false,
    executable: boolean = false,
    addPatternFunctions: boolean = false,
    transientColumnRefs?: ColumnReference[]
): IDataFlowArguments {
    const availableColumnsArguments = convertColumnRefToDataFlowColumn(distinctInputColumnRefs);
    const avaialbeCacheSinkArguments = convertCacheSinksToArguments(cacheSinks);
    const availableTransientColumnsArguments = (transientColumnRefs && convertColumnRefToDataFlowColumn(transientColumnRefs)) || [];
    const parameterArguments = parameters ? convertParametersToArguments(parameters) : [];
    let functions = limitedfunctions ? limitedfunctions : DataFlowExpressionFunctions.ALL_FUNCTIONS;
    if (addPatternFunctions) {
        functions = functions.concat(...DataFlowExpressionFunctions.PATTERN_FUNCTIONS);
    }
    if (addAggregateFunctions) {
        functions = functions.concat(...DataFlowExpressionFunctions.AGGREGATE_FUNCTIONS);
    }
    if (addWindowFunctions) {
        functions = functions.concat(...DataFlowExpressionFunctions.WINDOW_FUNCTIONS);
    }
    if (filterTypes) {
        functions = functions.filter((func) => filterTypes.includes(func.filterType));
    }
    if (executable) {
        functions = functions.filter((func) => func.isExecutable());
    }
    return {
        dataFlowFunctions: functions,
        dataFlowColumns: availableColumnsArguments,
        dataFlowTransientColumns: availableTransientColumnsArguments,
        dataFlowParameters: parameterArguments,
        dataFlowCacheSinks: avaialbeCacheSinkArguments
    };
}

export function generateArgumentsForUserDefinedFunction(
    userDefinedArguments?: DataFlowUserDefinedArgument[],
    limitedfunctions?: DataFlowFunctionArgument[],
    filterTypes?: string[],
    addWindowFunctions: boolean = false,
    addAggregateFunctions: boolean = false,
    executable: boolean = false,
    addPatternFunctions: boolean = false
): IDataFlowArguments {
    let functions = limitedfunctions ? limitedfunctions : DataFlowExpressionFunctions.ALL_FUNCTIONS;
    if (addPatternFunctions) {
        functions = functions.concat(...DataFlowExpressionFunctions.PATTERN_FUNCTIONS);
    }
    if (addAggregateFunctions) {
        functions = functions.concat(...DataFlowExpressionFunctions.AGGREGATE_FUNCTIONS);
    }
    if (addWindowFunctions) {
        functions = functions.concat(...DataFlowExpressionFunctions.WINDOW_FUNCTIONS);
    }
    if (filterTypes) {
        functions = functions.filter((func) => filterTypes.includes(func.filterType));
    }
    if (executable) {
        functions = functions.filter((func) => func.isExecutable());
    }
    return {
        dataFlowFunctions: functions,
        dataFlowColumns: [],
        dataFlowTransientColumns: [],
        dataFlowParameters: [],
        dataFlowCacheSinks: [],
        dataFlowUserDefinedArguments: userDefinedArguments
    };
}

export function assignDataFlowExpressionValue(target: DataFlowExpression, source: DataFlowExpression): void {
    if (target && source) {
        if (source.templateExpression) {
            target.templateExpression = source.templateExpression;
        } else {
            target.expression = source.expression;
        }
    }
}
